import React from 'react';
import { useBranchDetailsQuery } from '@/api/queries';
import type { BranchOption } from '@/pages/team/components/userFormModal';
import { UserFormModal } from '@/pages/team/components/userFormModal';
import { useCreateUser } from '@/pages/team/useCreateUser';
import { PATH } from '@/router/PATH';
import { getUser } from '@/utils';
import type { ActionSurfaceProps } from '../../controlCenterSurfaces';

/**
 * "Add user" from the launcher. See `AddBrandSurface` for the shape.
 *
 * The only gated one of the six — `gate: 'management'` in the registry — and
 * the only one whose form is caller-aware. Which roles may be assigned, and
 * whether the branch is locked to the caller's own, are derived inside
 * `useUserForm` from `callerRole` / `callerBranchId`, so those must be the real
 * session and not defaults: a launcher that handed the form an OrgAdmin's
 * privileges would show a BranchAdmin options the server will refuse.
 *
 * `isSelf` is `false` by construction. It exists to stop someone disabling or
 * demoting their own row, and a create has no row yet.
 *
 * Branch options come from the shared `useBranchDetailsQuery` — the same cached
 * query the team page reads, so opening this costs nothing the app was not
 * going to fetch anyway. Archived branches are excluded, as they are on the
 * team page: you do not post someone to a branch that has stopped trading.
 */
export const AddUserSurface: React.FC<ActionSurfaceProps> = ({
    onClose,
    onDone,
}) => {
    const [error, setError] = React.useState<string | null>(null);
    const { create, isCreating } = useCreateUser();
    const branchesQuery = useBranchDetailsQuery();
    const caller = getUser();

    const branchOptions = React.useMemo<BranchOption[]>(() => {
        if (!branchesQuery.data || !branchesQuery.data.success) {
            return [];
        }
        return branchesQuery.data.response.items.map((branch) => ({
            id: branch.id,
            name: branch.name,
        }));
    }, [branchesQuery.data]);

    // The gate refuses every session without a user, so this is a formality
    // that keeps the type honest rather than a case that renders.
    if (!caller) {
        return null;
    }

    return (
        <UserFormModal
            branchOptions={branchOptions}
            callerBranchId={caller.branchId ?? null}
            callerRole={caller.role}
            error={error}
            initialValues={null}
            isOpen
            isSelf={false}
            isSubmitting={isCreating}
            mode="create"
            onClose={onClose}
            onSubmit={async (input) => {
                setError(null);
                const created = await create(input);
                if (!created.ok) {
                    setError(created.message);
                    return;
                }
                onDone({
                    messageKey: 'controlCenter.outcome.userAdded',
                    messageParams: { name: input.name },
                    to: PATH.TEAM,
                    linkKey: 'controlCenter.outcome.openTeam',
                });
            }}
        />
    );
};
